// frontend/src/pages/admin/Reviews.js
//
// Moderation for the reviews that appear on the public product pages.
//
// routes/reviews.js has had GET /admin/all and PATCH /:id/visibility since the
// round that added them, with tests, and its own comment says ops needs to be
// able to take something down "without a database console". Nothing ever called
// either one: there was no page and no sidebar entry, so a defamatory or plainly
// fake review sat on a product page until someone opened psql. This is that
// screen.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';
import { format } from 'date-fns';
import { AlertTriangle, RefreshCw, Eye, EyeOff, Star } from 'lucide-react';
import theme from '../../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

const PAGE_SIZE = 25;

// The interceptor flattens the error body, so `error` is the machine code and
// `message` the sentence worth showing — `err.response.data` is not a thing
// here. Anything without `error` is a raw axios failure whose own `.message`
// reads "Request failed with status code 500".
const serverText = (err, fallback) =>
  (err && typeof err === 'object' && err.error) ? (err.message || err.error) : fallback;

function Stars({ rating }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, verticalAlign: 'middle' }} aria-label={`${rating}/5`}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star key={n} size={14}
          color={n <= rating ? theme.gold : theme.sandMid}
          fill={n <= rating ? theme.gold : 'none'} />
      ))}
    </span>
  );
}

export default function AdminReviews() {
  const { t } = useTranslation();
  // date-fns defaults to en-US when handed no locale, which is how the Greek
  // panel ended up printing English month names elsewhere.
  const dateLocale = getDateLocale();

  const [reviews, setReviews]       = useState([]);
  const [count, setCount]           = useState(0);
  const [hiddenCount, setHidden]    = useState(null);
  const [filter, setFilter]         = useState('all');   // 'all' | 'hidden'
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [busyId, setBusyId]         = useState(null);
  // Switching filter twice quickly leaves two loads in flight and they do not
  // answer in order. An older answer landing last would show the full list under
  // the 'hidden' tab — here that reads as "we have hidden everything".
  const loadSeq = useRef(0);

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // A narrower filter may not have the page currently being shown.
  useEffect(() => { setPage(1); }, [filter]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const seq = ++loadSeq.current;
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
    if (filter === 'hidden') params.set('hidden', 'true');
    api.get(`/reviews/admin/all?${params}`)
      .then(d => {
        if (seq !== loadSeq.current) return;   // a newer filter is already loading
        setReviews(Array.isArray(d?.reviews) ? d.reviews : []);
        setCount(Number(d?.count) || 0);
      })
      // An outage rendered as an empty list reads as "nobody has reviewed
      // anything", which would hide exactly the review someone reported.
      .catch(err => {
        if (seq !== loadSeq.current) return;
        setLoadError(serverText(err, t('admin.reviews.loadFailedDetail')));
      })
      .finally(() => { if (seq === loadSeq.current) setLoading(false); });
  }, [page, filter, t]);

  // Its own one-row query so the badge survives being on the 'hidden' tab and
  // does not have to be counted out of whatever happens to be on screen.
  const loadHiddenCount = useCallback(() => {
    api.get('/reviews/admin/all?hidden=true&limit=1')
      .then(d => setHidden(Number(d?.count) || 0))
      .catch(() => setHidden(null));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHiddenCount(); }, [loadHiddenCount]);

  // Hiding is a moderation decision on public content, and the API records who
  // did it and why. Asking for the reason here rather than sending null is what
  // makes that record worth having when the customer asks why their review
  // disappeared — the server stores it, and the audit log keeps it.
  const setVisibility = async (review, hidden) => {
    let reason = null;
    if (hidden) {
      reason = window.prompt(t('admin.reviews.hidePrompt'));
      // Cancel is null and means "changed my mind".
      if (reason === null) return;
      // An empty box used to be allowed through as a deliberate "no reason
      // given". It is no longer: the public caption promises we hide a review
      // only when it is unlawful or is not about a rental, and a hide with no
      // stated reason is the one thing that cannot be told apart from hiding a
      // bad review. The server rejects it too — this only saves the round trip.
      if (!reason.trim()) {
        toast.error(t('admin.reviews.reasonRequired'), { duration: 6000 });
        return;
      }
    } else if (!window.confirm(t('admin.reviews.unhideConfirm'))) {
      return;
    }

    setBusyId(review.id);
    try {
      const updated = await api.patch(`/reviews/${review.id}/visibility`, { hidden, reason });
      // Updated in place rather than dropped, even when its new state no longer
      // matches the active filter: having a review vanish the instant it is
      // hidden makes it impossible to check what you just did.
      setReviews(prev => prev.map(r => (r.id === review.id ? { ...r, ...updated } : r)));
      toast.success(hidden ? t('admin.reviews.hidden') : t('admin.reviews.unhidden'));
      loadHiddenCount();
    } catch (err) {
      toast.error(serverText(err, t('admin.reviews.updateFailed')), { duration: 8000 });
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 28 }}>
          <div>
            <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, margin: 0 }}>{t('admin.reviews.title')}</h1>
            <p style={{ color: theme.grey400, margin: '4px 0 0' }}>{t('admin.reviews.subtitle')}</p>
          </div>
          <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 10, padding: '14px 24px', minWidth: 120, textAlign: 'center' }}>
            <div style={{ fontFamily: theme.font.display, fontSize: 34, lineHeight: 1, color: hiddenCount ? theme.redBrick : theme.greenDeep }}>
              {hiddenCount === null ? '—' : hiddenCount}
            </div>
            <div style={{ fontSize: 11, color: theme.grey400, marginTop: 6, letterSpacing: '0.06em' }}>{t('admin.reviews.statHidden')}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24, alignItems: 'center' }}>
          {['all', 'hidden'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '8px 18px',
              border: filter === f ? 'none' : `1px solid ${theme.sandMid}`,
              background: filter === f ? theme.ink : 'transparent',
              color: filter === f ? theme.white : theme.grey600,
              borderRadius: 100, fontSize: 11, letterSpacing: '0.06em', cursor: 'pointer', fontFamily: theme.font.body, whiteSpace: 'nowrap',
            }}>
              {t(`admin.reviews.filter.${f}`)}
            </button>
          ))}
          <button onClick={() => { load(); loadHiddenCount(); }} title={t('admin.reviews.refresh')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', padding: '8px 16px', border: `1px solid ${theme.sand}`, background: theme.white, borderRadius: 4, cursor: 'pointer', fontSize: 12, color: theme.grey600, fontFamily: theme.font.body }}>
            <RefreshCw size={13} /> {t('admin.reviews.refresh')}
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 100, color: theme.gold, fontFamily: theme.font.display, fontSize: 24 }}>{t('admin.reviews.loading')}</div>
        ) : loadError ? (
          <div style={{ background: theme.white, border: `1px solid ${theme.redWash}`, borderRadius: 10, padding: 48, textAlign: 'center' }}>
            <AlertTriangle size={26} style={{ color: theme.redBrick }} />
            <div style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.ink, margin: '10px 0 6px' }}>{t('admin.reviews.loadFailedTitle')}</div>
            <p style={{ color: theme.grey400, margin: '0 0 20px', fontSize: 13 }}>{loadError}</p>
            <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: theme.ink, color: theme.white, border: 'none', padding: '12px 26px', borderRadius: 8, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
              <RefreshCw size={13} /> {t('admin.reviews.retry')}
            </button>
          </div>
        ) : reviews.length === 0 ? (
          <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 10, padding: 72, textAlign: 'center' }}>
            <div style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.grey400 }}>
              {filter === 'hidden' ? t('admin.reviews.emptyHidden') : t('admin.reviews.empty')}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {reviews.map(r => {
              const busy = busyId === r.id;
              return (
                <div key={r.id} style={{
                  background: theme.white,
                  border: `1px solid ${theme.creamBone}`,
                  // A hidden review is off the public site; the stripe is what
                  // separates "this is live on a product page right now" from
                  // "we already took this down" in a mixed list.
                  borderLeft: `3px solid ${r.hidden ? theme.redBrick : theme.gold}`,
                  borderRadius: 8, padding: '20px 24px',
                  opacity: r.hidden ? 0.75 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Stars rating={Number(r.rating) || 0} />
                        <span style={{ fontFamily: theme.font.display, fontSize: 20, color: theme.ink }}>{r.product_name || '—'}</span>
                        {r.hidden && (
                          <span style={{ background: theme.state.danger.bg, color: theme.state.danger.color, padding: '3px 12px', borderRadius: 100, fontSize: 10, letterSpacing: '0.05em', fontWeight: 500 }}>
                            {t('admin.reviews.badgeHidden')}
                          </span>
                        )}
                      </div>
                      <div style={{ color: theme.grey400, fontSize: 12, marginTop: 6 }}>
                        {r.created_at ? format(new Date(r.created_at), 'd MMM yyyy, HH:mm', { locale: dateLocale }) : '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 13 }}>
                      <div style={{ color: theme.ink, fontWeight: 500 }}>{r.full_name || t('admin.reviews.deletedAuthor')}</div>
                      {r.email && <div style={{ color: theme.grey400, fontSize: 12 }}>{r.email}</div>}
                    </div>
                  </div>

                  <p style={{ color: theme.ink700, fontSize: 14, lineHeight: 1.75, margin: '16px 0 0', whiteSpace: 'pre-wrap' }}>
                    {r.comment || <em style={{ color: theme.grey400 }}>{t('admin.reviews.noComment')}</em>}
                  </p>

                  {/* Why it came down, and who decided. Without this the only
                      answer to "why was my review removed" is a shrug. */}
                  {r.hidden && (
                    <div style={{ marginTop: 14, background: theme.redWashPale, border: `1px solid ${theme.redBorder}`, borderRadius: 6, padding: '10px 14px', fontSize: 12.5, color: theme.redSoft }}>
                      {r.hidden_reason
                        ? t('admin.reviews.hiddenBecause', { reason: r.hidden_reason })
                        : t('admin.reviews.hiddenNoReason')}
                      {r.hidden_by_name && <> · {t('admin.reviews.hiddenBy', { name: r.hidden_by_name })}</>}
                      {r.hidden_at && <> · {format(new Date(r.hidden_at), 'd MMM yyyy, HH:mm', { locale: dateLocale })}</>}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
                    {r.hidden ? (
                      <button onClick={() => setVisibility(r, false)} disabled={busy}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: theme.white, color: theme.greenDeep, border: `1px solid ${theme.greenDeep}`, padding: '9px 18px', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.5 : 1, fontSize: 12, fontFamily: theme.font.body }}>
                        <Eye size={13} /> {t('admin.reviews.unhide')}
                      </button>
                    ) : (
                      <button onClick={() => setVisibility(r, true)} disabled={busy}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: theme.white, color: theme.redBrick, border: `1px solid ${theme.redBrick}`, padding: '9px 18px', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.5 : 1, fontSize: 12, fontFamily: theme.font.body }}>
                        <EyeOff size={13} /> {t('admin.reviews.hide')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !loadError && count > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, fontSize: 13, color: theme.grey500 }}>
            <span>
              {t('admin.reviews.pagination', {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, count),
                total: count,
              })}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.reviews.prev')}
              </button>
              <span style={{ padding: '8px 12px', background: theme.ink, color: theme.white, borderRadius: 4, fontSize: 12 }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= count}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page * PAGE_SIZE >= count ? 'not-allowed' : 'pointer', opacity: page * PAGE_SIZE >= count ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.reviews.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
